'use client'

import { hasAnyFraxlendPause } from '@/features/isolated/types/FraxlendPairPauseState'
import { assets } from '@/ui/assets'
import { CuratorIcon } from '@/ui/atoms/curator-icon/CuratorIcon'
import { Dash } from '@/ui/atoms/dash/Dash'
import { TokenImage } from '@/ui/atoms/token-image/TokenImage'
import { Tooltip, TooltipContentLong, TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import { TooltipContentLayout } from '@/ui/atoms/tooltip/TooltipContentLayout'
import { FraxlendPauseIndicator } from '@/ui/molecules/fraxlend-pause-indicator/FraxlendPauseIndicator'
import { FrozenPill } from '@/ui/molecules/frozen-pill/FrozenPill'
import { Info } from '@/ui/molecules/info/Info'
import { SupplyApyTooltip } from '@/ui/molecules/supply-apy-tooltip/SupplyApyTooltip'
import { cn } from '@/ui/utils/style'
import { useWindowVirtualizer } from '@tanstack/react-virtual'
import { ChevronDown, ChevronUp } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import type { ReactNode } from 'react'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { InWalletCell } from './InWalletCell'
import type { MarketEntrySections, MarketEntryViewModel } from './types'
import { getPointsVisibility } from './utils/getPointsVisibility'
import { getPartnerPointKey } from './utils/partnerPointsKey'
import { type PointsDisplayInfo, resolvePointsDisplay } from './utils/resolvePointsDisplay'

export interface VirtualMarketTableProps {
  entries: MarketEntryViewModel[]
  sections: MarketEntrySections
  rowHeight?: number
  renderExposure?: (entry: MarketEntryViewModel) => ReactNode
  renderUtilization?: (entry: MarketEntryViewModel) => ReactNode
  renderHoldings?: (entry: MarketEntryViewModel) => ReactNode
  getPointsInfo?: (entryId: string) => PointsDisplayInfo | undefined
}

type SortField = 'apy' | 'apy30d' | 'points' | 'tvl' | 'utilization' | 'userPosition' | 'walletBalance' | 'holdings'
type SortDirection = 'asc' | 'desc'

function parseValue(value: string): number {
  const cleaned = value.replace(/[^0-9.-]/g, '')
  return Number.parseFloat(cleaned) || 0
}

export function VirtualMarketTable({
  entries,
  sections,
  rowHeight = 72,
  renderExposure,
  renderUtilization,
  renderHoldings,
  getPointsInfo,
}: VirtualMarketTableProps) {
  const listRef = useRef<HTMLDivElement>(null)
  const [sortField, setSortField] = useState<SortField | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [listOffsetTop, setListOffsetTop] = useState(0)

  useLayoutEffect(() => {
    const updateOffset = () => {
      if (listRef.current) {
        setListOffsetTop(listRef.current.offsetTop)
      }
    }

    updateOffset()
    window.addEventListener('resize', updateOffset)
    return () => window.removeEventListener('resize', updateOffset)
  }, [])

  const sortedEntries = useMemo(() => {
    if (sortField === null) {
      return entries
    }

    return [...entries].sort((a, b) => {
      let aVal = 0
      let bVal = 0

      switch (sortField) {
        case 'apy':
          aVal = parseValue(a.apy.primary)
          bVal = parseValue(b.apy.primary)
          break
        case 'apy30d':
          aVal = a.apy30d?.sortValue ?? 0
          bVal = b.apy30d?.sortValue ?? 0
          break
        case 'points': {
          const getPointMetrics = (entry: MarketEntryViewModel) => {
            const pointsInfo = getPointsInfo?.(entry.id)
            const { partnerPoints, hasHypurrPoints, hasPartnerPoints, isEarnVault } = resolvePointsDisplay({
              entryPartnerPoints: entry.apyPoints?.partnerPoints,
              pointsInfo,
              isEarnVault: entry.apyMarketType === 'evault',
            })
            const { hasEarnVaultPoints } = getPointsVisibility({
              hasHypurrPoints,
              hasPartnerPoints,
              isEarnVault,
            })
            const hypurrMultiplier = pointsInfo?.multiplier ?? -1
            const partnerMultiplier = partnerPoints.reduce((maxMultiplier, partner) => {
              const multiplier =
                partner.lend?.multiplier ?? partner.deposit?.multiplier ?? partner.collateral?.multiplier ?? 0
              return Math.max(maxMultiplier, multiplier)
            }, 0)

            const rank = hasHypurrPoints || hasEarnVaultPoints ? 2 : hasPartnerPoints ? 1 : 0
            return { rank, hypurrMultiplier, partnerMultiplier }
          }

          const aMetrics = getPointMetrics(a)
          const bMetrics = getPointMetrics(b)

          if (aMetrics.rank !== bMetrics.rank) {
            return sortDirection === 'desc' ? bMetrics.rank - aMetrics.rank : aMetrics.rank - bMetrics.rank
          }

          if (aMetrics.hypurrMultiplier !== bMetrics.hypurrMultiplier) {
            return sortDirection === 'desc'
              ? bMetrics.hypurrMultiplier - aMetrics.hypurrMultiplier
              : aMetrics.hypurrMultiplier - bMetrics.hypurrMultiplier
          }

          if (aMetrics.partnerMultiplier !== bMetrics.partnerMultiplier) {
            return sortDirection === 'desc'
              ? bMetrics.partnerMultiplier - aMetrics.partnerMultiplier
              : aMetrics.partnerMultiplier - bMetrics.partnerMultiplier
          }

          return 0
        }
        case 'tvl':
          aVal = a.tvl.sortValue ?? 0
          bVal = b.tvl.sortValue ?? 0
          break
        case 'utilization':
          aVal = parseValue(a.utilization.primary)
          bVal = parseValue(b.utilization.primary)
          break
        case 'userPosition':
          aVal = parseValue(a.userPosition.primary)
          bVal = parseValue(b.userPosition.primary)
          break
        case 'walletBalance':
          aVal = parseValue(a.walletBalance.primary)
          bVal = parseValue(b.walletBalance.primary)
          break
        case 'holdings':
          aVal = a.holdingsSortValue ?? 0
          bVal = b.holdingsSortValue ?? 0
          break
      }

      return sortDirection === 'desc' ? bVal - aVal : aVal - bVal
    })
  }, [entries, getPointsInfo, sortField, sortDirection])

  const virtualizer = useWindowVirtualizer({
    count: sortedEntries.length,
    estimateSize: () => rowHeight,
    overscan: 5,
    scrollMargin: listOffsetTop,
  })

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'desc' ? 'asc' : 'desc'))
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    const isActive = sortField === field
    return (
      <div className="-space-y-1 flex flex-col">
        <ChevronUp
          className={cn(
            'h-3 w-3',
            isActive && sortDirection === 'asc' ? 'text-foreground' : 'text-muted-foreground/40',
          )}
        />
        <ChevronDown
          className={cn(
            'h-3 w-3',
            isActive && sortDirection === 'desc' ? 'text-foreground' : 'text-muted-foreground/40',
          )}
        />
      </div>
    )
  }

  const gridCols = useMemo(() => {
    const cols = ['minmax(0,1.2fr)']
    cols.push('minmax(0,1fr)')
    if (sections.show30dApy) cols.push('minmax(0,1fr)')
    if (sections.showTvl) cols.push('minmax(0,1fr)')
    if (sections.showUtilization) cols.push('minmax(0,1fr)')
    if (sections.showCurator) cols.push('minmax(0,0.6fr)')
    if (sections.showExposure) cols.push('minmax(0,0.6fr)')
    cols.push('minmax(0,0.6fr)')
    if (sections.showHoldings) cols.push('minmax(0,0.8fr)')
    if (sections.showWalletBalance) cols.push('minmax(0,1fr)')
    if (sections.showUserPosition) cols.push('minmax(0,1fr)')
    if (sections.showBorrowDetails) cols.push('minmax(0,1fr)', 'minmax(0,1fr)')
    return cols.join(' ')
  }, [sections])

  return (
    <div className="flex flex-col rounded-2xl border border-border bg-panel">
      <div
        className="sticky top-12 z-10 grid items-center gap-3 rounded-t-2xl border-border border-b bg-card px-5 pt-5 pb-2 text-muted-foreground text-xs"
        style={{ gridTemplateColumns: gridCols }}
      >
        <div>Asset</div>
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            className={cn('flex items-center gap-1 hover:text-foreground', sortField === 'apy' && 'text-foreground')}
            onClick={() => handleSort('apy')}
          >
            APY <SortIcon field="apy" />
          </button>
          <Info size={12}>Current total supply APY, including lending APY and any intrinsic asset APY.</Info>
        </div>
        {sections.show30dApy && (
          <div className="flex items-center justify-end gap-1">
            <button
              type="button"
              className={cn(
                'flex items-center gap-1 hover:text-foreground',
                sortField === 'apy30d' && 'text-foreground',
              )}
              onClick={() => handleSort('apy30d')}
            >
              30d APY <SortIcon field="apy30d" />
            </button>
            <Info size={12}>30-day average lending APY. Intrinsic asset APY is not tracked in this average.</Info>
          </div>
        )}
        {sections.showTvl && (
          <div className="flex items-center justify-end gap-1">
            <button
              type="button"
              className={cn('flex items-center gap-1 hover:text-foreground', sortField === 'tvl' && 'text-foreground')}
              onClick={() => handleSort('tvl')}
            >
              TVL <SortIcon field="tvl" />
            </button>
            <Info size={12}>Total value currently deposited in this vault.</Info>
          </div>
        )}
        {sections.showUtilization && (
          <div className="flex items-center justify-end gap-1">
            <button
              type="button"
              className={cn(
                'flex items-center gap-1 hover:text-foreground',
                sortField === 'utilization' && 'text-foreground',
              )}
              onClick={() => handleSort('utilization')}
            >
              Util. <SortIcon field="utilization" />
            </button>
            <Info size={12}>Percentage of deposited assets currently being borrowed.</Info>
          </div>
        )}
        {sections.showCurator && (
          <div className="flex items-center justify-end gap-1">
            Curator <Info size={12}>Manages market configuration, collateral parameters, and risk limits.</Info>
          </div>
        )}
        {sections.showExposure && (
          <div className="flex items-center justify-end gap-1">
            Exposure <Info size={12}>The collateral types that back borrowed assets in this market.</Info>
          </div>
        )}
        <button
          type="button"
          className={cn(
            'flex items-center justify-end gap-1 hover:text-foreground',
            sortField === 'points' && 'text-foreground',
          )}
          onClick={() => handleSort('points')}
        >
          Points <SortIcon field="points" />
        </button>
        {sections.showHoldings && (
          <div className="flex items-center justify-end gap-1">
            <button
              type="button"
              className={cn(
                'flex items-center gap-1 hover:text-foreground',
                sortField === 'holdings' && 'text-foreground',
              )}
              onClick={() => handleSort('holdings')}
            >
              My Holdings <SortIcon field="holdings" />
            </button>
            <Info size={12}>Your total value across deposited positions and wallet balance.</Info>
          </div>
        )}
        {sections.showWalletBalance && (
          <button
            type="button"
            className={cn(
              'flex items-center justify-end gap-1 hover:text-foreground',
              sortField === 'walletBalance' && 'text-foreground',
            )}
            onClick={() => handleSort('walletBalance')}
          >
            In Wallet <SortIcon field="walletBalance" />
          </button>
        )}
        {sections.showUserPosition && (
          <button
            type="button"
            className={cn(
              'flex items-center justify-end gap-1 hover:text-foreground',
              sortField === 'userPosition' && 'text-foreground',
            )}
            onClick={() => handleSort('userPosition')}
          >
            Position <SortIcon field="userPosition" />
          </button>
        )}
        {sections.showBorrowDetails && (
          <>
            <div className="text-right">Borrow APY</div>
            <div className="text-right">Borrowed</div>
          </>
        )}
      </div>

      <div
        ref={listRef}
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const entry = sortedEntries[virtualRow.index]
          if (!entry) return null

          const hasPosition = entry.userPosition.primary !== '-' && parseValue(entry.userPosition.primary) > 0

          const isLastRow = virtualRow.index === sortedEntries.length - 1

          return (
            <Link
              key={entry.id}
              href={entry.href}
              className={cn(
                'absolute top-0 left-0 grid w-full cursor-pointer items-center gap-3 px-5 text-inherit no-underline transition-colors hover:bg-white/[0.02]',
                !isLastRow && 'border-white/5 border-b',
                hasPosition && sections.showUserPosition && 'bg-gradient-to-r from-gradient-yellow/5 to-gradient-green/5',
              )}
              style={{
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
                gridTemplateColumns: gridCols,
              }}
            >
              <div className="flex items-center gap-3">
                {entry.token.logoSrc && (
                  <TokenImage
                    src={entry.token.logoSrc}
                    alt={entry.token.symbol}
                    width={32}
                    height={32}
                    className="h-8 w-8 shrink-0 rounded-full"
                    fallbackSrc={entry.token.logoFallbackSrc}
                    tokenAddress={entry.token.address}
                  />
                )}
                <div className="flex min-w-0 flex-col">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <span className="truncate font-semibold text-foreground text-sm">{entry.token.symbol}</span>
                    {entry.reserveStatus === 'frozen' && (
                      <span
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                        }}
                        className="flex shrink-0"
                      >
                        <FrozenPill />
                      </span>
                    )}
                    {entry.fraxlendPauseState && hasAnyFraxlendPause(entry.fraxlendPauseState) && (
                      <span
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                        }}
                        className="flex shrink-0"
                      >
                        <FraxlendPauseIndicator state={entry.fraxlendPauseState} />
                      </span>
                    )}
                  </div>
                  {entry.token.subtitle && (
                    <span className="truncate text-muted-foreground text-xs">{entry.token.subtitle}</span>
                  )}
                </div>
              </div>

              <div className="flex flex-col items-end justify-center">
                {(() => {
                  const isCollateralOnlyZeroApy = entry.collateralOnly && parseValue(entry.apy.primary) <= 0

                  if (isCollateralOnlyZeroApy) {
                    return (
                      <Info size={14} className="cursor-help">
                        This asset is collateral only and does not earn borrowing-driven APR.
                      </Info>
                    )
                  }

                  const apyValue = <span className="font-medium text-foreground text-sm">{entry.apy.primary}</span>

                  const content = <div className="cursor-pointer">{apyValue}</div>

                  return entry.apyBreakdown ? (
                    <SupplyApyTooltip
                      breakdown={entry.apyBreakdown}
                      collateralOnly={entry.collateralOnly}
                      marketType={entry.apyMarketType}
                      showPointsSection={false}
                      vaultAddress={entry.vaultAddress}
                    >
                      {content}
                    </SupplyApyTooltip>
                  ) : (
                    content
                  )
                })()}
              </div>

              {sections.show30dApy && (
                <div className="flex flex-col items-end justify-center">
                  {entry.apy30d && entry.apy30d.primary !== '-' ? (
                    <span className="font-medium text-foreground text-sm">{entry.apy30d.primary}</span>
                  ) : (
                    <Dash />
                  )}
                </div>
              )}

              {sections.showTvl && (
                <div className="flex min-w-0 flex-col items-end">
                  {entry.tvl.primary === '-' ? (
                    <Dash />
                  ) : (
                    <>
                      <span className="font-medium text-foreground text-sm">{entry.tvl.primary}</span>
                      {entry.tvl.secondary && (
                        <span className="max-w-[100px] truncate text-muted-foreground text-xs">
                          {entry.tvl.secondary}
                        </span>
                      )}
                    </>
                  )}
                </div>
              )}

              {sections.showUtilization && (
                <div className="flex justify-end text-right">
                  {renderUtilization ? (
                    renderUtilization(entry)
                  ) : entry.utilization.primary === '-' ? (
                    <Dash />
                  ) : (
                    <span className="font-medium text-foreground text-sm">{entry.utilization.primary}</span>
                  )}
                </div>
              )}

              {sections.showCurator && (
                <div className="flex justify-end">
                  <CuratorIcon logo={entry.token.curatorLogoSrc} name={entry.token.curatorName} />
                </div>
              )}

              {sections.showExposure && (
                <div className="flex justify-end">{renderExposure ? renderExposure(entry) : null}</div>
              )}

              <div className="flex justify-end">
                {(() => {
                  const pointsInfo = getPointsInfo?.(entry.id)
                  const { partnerPoints, hasHypurrPoints, hasPartnerPoints, isEarnVault } = resolvePointsDisplay({
                    entryPartnerPoints: entry.apyPoints?.partnerPoints,
                    pointsInfo,
                    isEarnVault: entry.apyMarketType === 'evault',
                  })
                  const { hasEarnVaultPoints, hasVisiblePoints } = getPointsVisibility({
                    hasHypurrPoints,
                    hasPartnerPoints,
                    isEarnVault,
                  })

                  if (!hasVisiblePoints) {
                    return <Dash />
                  }

                  const pointsIcons = (
                    <div className="flex items-center">
                      {partnerPoints.map((partner, idx) => (
                        <div
                          key={getPartnerPointKey(partner)}
                          className="relative h-4 w-4 rounded-full border border-background"
                          style={{
                            marginLeft: idx > 0 ? '-4px' : '0',
                            zIndex: 9 + idx,
                          }}
                        >
                          <Image
                            src={assets[partner.icon as keyof typeof assets]}
                            alt={partner.title}
                            className="h-full w-full rounded-full"
                          />
                        </div>
                      ))}
                      {(hasHypurrPoints || hasEarnVaultPoints) && (
                        <div
                          className="relative h-4 w-4 rounded-full border border-background"
                          style={{
                            marginLeft: partnerPoints.length > 0 ? '-4px' : '0',
                            zIndex: 20,
                          }}
                        >
                          <Image src={assets.hypurrPawNoShadow} alt="HP" className="h-full w-full" />
                        </div>
                      )}
                    </div>
                  )

                  return (
                    <Tooltip>
                      <TooltipTrigger
                        asChild
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                        }}
                      >
                        <div className="cursor-help">{pointsIcons}</div>
                      </TooltipTrigger>
                      <TooltipContentLong>
                        <TooltipContentLayout>
                          <TooltipContentLayout.Header>
                            <TooltipContentLayout.Title>Eligible for points</TooltipContentLayout.Title>
                          </TooltipContentLayout.Header>
                          <TooltipContentLayout.Body>
                            {hasEarnVaultPoints && (
                              <span className="text-sm">
                                This vault is earning points in accordance with the strategy utilized. Check the
                                underlying lending markets for clarity on points multipliers.
                              </span>
                            )}
                            {hasHypurrPoints && (
                              <div className="mt-2 flex items-center gap-2">
                                <Image src={assets.hypurrPawNoShadow} alt="HP" className="h-4 w-4" />
                                <span className="text-sm">
                                  {pointsInfo?.multiplier ? `${pointsInfo.multiplier}x ` : ''}HypurrFi Points
                                </span>
                              </div>
                            )}
                            {partnerPoints.map((partner) => {
                              const multiplier =
                                partner.lend?.multiplier ??
                                partner.deposit?.multiplier ??
                                partner.collateral?.multiplier
                              return (
                                <div key={getPartnerPointKey(partner)} className="mt-2 flex items-center gap-2">
                                  <Image
                                    src={assets[partner.icon as keyof typeof assets]}
                                    alt={partner.title}
                                    className="h-4 w-4"
                                  />
                                  <span className="text-sm">
                                    {multiplier ? `${multiplier}x ` : ''}
                                    {partner.title}
                                  </span>
                                </div>
                              )
                            })}
                          </TooltipContentLayout.Body>
                        </TooltipContentLayout>
                      </TooltipContentLong>
                    </Tooltip>
                  )
                })()}
              </div>

              {sections.showHoldings && (
                <div className="flex justify-end">{renderHoldings ? renderHoldings(entry) : <Dash />}</div>
              )}

              {sections.showWalletBalance && (
                <InWalletCell amount={entry.walletBalanceAmount} symbol={entry.walletBalanceSymbol} />
              )}

              {sections.showUserPosition && (
                <div className="flex min-w-0 flex-col items-end">
                  {hasPosition ? (
                    <>
                      <span className="font-medium text-green-400 text-sm">{entry.userPosition.primary}</span>
                      {entry.userPosition.secondary && (
                        <span className="max-w-[100px] truncate text-muted-foreground text-xs">
                          {entry.userPosition.secondary}
                        </span>
                      )}
                    </>
                  ) : (
                    <Dash />
                  )}
                </div>
              )}

              {sections.showBorrowDetails && entry.borrowDetails && (
                <>
                  <div className="text-right">
                    <span className="font-medium text-foreground text-sm">{entry.borrowDetails.borrowApy.primary}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-medium text-foreground text-sm">
                      {entry.borrowDetails.totalBorrowed.primary}
                    </span>
                  </div>
                </>
              )}
            </Link>
          )
        })}
      </div>

      {sortedEntries.length === 0 && (
        <div className="flex items-center justify-center p-8 text-muted-foreground">No entries found</div>
      )}
    </div>
  )
}
